import React, { useState, useEffect, useCallback } from "react";
import styles from "./CustomerList.module.sass";
import cn from "classnames";
import { useNavigate } from "react-router-dom"; 
import Card from "../../components/Card";
import Form from "../../components/Form";
import Filters from "../../components/Filters";
import Table from "./Table";
import Settings from "./Settings";
import Icon from "../../components/Icon";
import { getStudentList } from "../../services/student.service";

const StudentList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // État pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récents",
    formationId: null,
    fileStatuses: []
  });

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // Construire les filtres
      let filterParts = [];
      
      // Filtre de recherche
      if (search.trim()) {
        filterParts.push(`(first_name ~ "${search}" || last_name ~ "${search}" || email ~ "${search}")`);
      }
      
      // Filtre par formation
      if (filters.formationId) {
        filterParts.push(`formation = "${filters.formationId}"`);
      }
      
      // Filtre par état du dossier
      if (filters.fileStatuses.length > 0) {
        const fileStatusFilters = filters.fileStatuses.map(fs => `file_status = "${fs}"`).join(' || ');
        filterParts.push(`(${fileStatusFilters})`);
      }
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : '';
      
      // Déterminer le tri
      let sortString = "-created"; // Par défaut : plus récents
      if (filters.sorting === "Plus anciens") {
        sortString = "+created";
      }

      const options = {
        page: currentPage,
        perPage: 10,
        sort: sortString,
      };

      if (filterString) {
        options.filter = filterString;
      }

      const result = await getStudentList(options);

      setStudents(result.items || []);
      setTotalPages(result.totalPages || 1);
      setNoResults(result.items.length === 0 && (!!search || filters.formationId || filters.fileStatuses.length > 0));
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des étudiants:", err);
        setError("Impossible de charger les étudiants. Veuillez réessayer.");
        setStudents([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, filters]);
  
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);
  
  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Remettre à la première page lors du changement de filtres
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    fetchStudents();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStudentSelect = (id) => {
    const student = students.find((student) => student.id === id);
    if (student) {
      setSelectedStudent(student);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Élèves"
        classTitle={cn("title-purple", styles.title)}
        classCardHead={styles.head}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par nom, prénom ou email"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/students/create")}>Créer</button>
            <Filters
              className={styles.filters}
              title="Filtrer"
            >
              <Settings onFiltersChange={handleFiltersChange} />
            </Filters>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement des élèves...</div>
        ) : (
          <div className={styles.row}>
            {noResults && students.length === 0 && (
              <div className={styles.empty}>
                Aucun élève trouvé pour les critères sélectionnés.
              </div>
            )}
            {!noResults && students.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucun élève disponible.
              </div>
            )}
            {students.length > 0 && (
              <>
                <Table
                  className={styles.table}
                  items={students}
                  onStudentSelect={handleStudentSelect}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />

                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                    disabled={currentPage === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
};

export default StudentList;